import { api } from '../api.ts';
import { useGame, money, fmt, CLASS_INFO } from '../state.tsx';
import { Loading, useLoad, go, PlayerLink, Panel } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { BossesTab } from './Bosses.tsx';
import { PublicEnemyTab } from './PublicEnemy.tsx';
import { FightClash } from '../fx/FightClash.tsx';
import { fx } from '../fx/fx.tsx';
import { sequence } from '../fx/sound.ts';

/** Floating cash/XP numbers after the fight animation has played. */
function floatAfter(cash: number, xp: number, ms = 1300) {
  if (cash) fx.float(`${cash > 0 ? '+' : '−'}${money(Math.abs(cash))}`, cash > 0 ? 'cash' : 'loss', null, ms);
  if (xp > 0) fx.float(`+${xp} XP`, 'xp', null, ms + 180);
}

export function useAttack() {
  const { act, setFlash, me, content } = useGame();
  const doAttack = async (targetId: number, name: string, portrait = 0): Promise<any> => {
    const streakBefore = me.fightStreak ?? 0;
    const r = await act('/api/fight/attack', { targetId });
    if (!r) return null;
    floatAfter(r.won ? r.cash + (r.rivalBonus ?? 0) : -r.cash, r.won ? r.xp : 0);
    if (r.streakBonus) {
      fx.stamp(`${r.streak} in a row!`, `streak bonus +${money(r.streakBonus.cash)}`, 'gold', 1500);
      sequence([[1500, 'streak']]);
      fx.float(`+${money(r.streakBonus.cash)}`, 'cash', null, 1900);
    }
    if (r.newRivalry) fx.stamp('New rival', `${name} · beat them for +${content.rules.rivalry.bonusPct}% cash`, 'bad', 1500);
    setFlash({
      kind: r.won ? 'good' : 'bad',
      title: <>{r.rival && <span className="rival-tag">Rival</span>}{r.won ? `You WON the fight against ${name}!` : `You LOST the fight against ${name}.`}</>,
      text: (
        <>
          <FightClash key={Date.now()} left={{ seed: me.portrait, playerId: me.id, name: 'You' }} right={{ seed: portrait, playerId: targetId, name }} won={r.won} dealt={r.damageDealt} taken={r.damageTaken} myHealth={{ after: Math.max(0, me.health - r.damageTaken), max: me.maxHealth }} />
          {r.won ? <>You took <span className="money">{money(r.cash)}</span>{r.rivalBonus > 0 && <> plus a <span className="money">{money(r.rivalBonus)}</span> rival bonus</>} and gained <span className="xp">{r.xp} experience</span>. </> : <>They took <span className="bad">{money(r.cash)}</span> from you. </>}
          {r.won && r.streak > 1 && <div className="gold">Winning streak: {r.streak}{r.streakBonus ? <> · streak bonus <span className="money">{money(r.streakBonus.cash)}</span> and {r.streakBonus.xp} XP{r.streakNews ? ' · you made the news!' : ''}</> : <> · {content.rules.fightStreak.every - (r.streak % content.rules.fightStreak.every)} more for a bonus</>}</div>}
          {!r.won && streakBefore >= content.rules.fightStreak.every && <div className="bad">Your {streakBefore}-fight winning streak is over.</div>}
          {r.newRivalry && <div className="bad"><b>{name} is now your rival.</b> Beating a rival pays {content.rules.rivalry.bonusPct}% extra cash (and {content.rules.rivalry.bonusPct}% extra XP when you attack).</div>}
          You dealt {r.damageDealt} damage and took {r.damageTaken}.
          <div className="small muted">Your attack strength {fmt(r.attackPower)} vs. their defense {fmt(r.defensePower)}.</div>
          {r.levelUps.map((u: any) => <div key={u.level} className="gold">LEVEL UP! You are now level {u.level}. <a onClick={() => go(`profile/${me.id}`)}>Spend skill points</a>.</div>)}
          {r.won && <div style={{ marginTop: 4 }}><button className="btn small" onClick={() => doAttack(targetId, name, portrait)}>Attack again</button></div>}
        </>
      ),
    });
    return r;
  };
  return doAttack;
}

export function FightPage({ tab = 'fight' }: { tab?: string }) {
  const { me, content } = useGame();
  if (!me) return <Loading />;
  const r = content.rules;
  return (
    <div>
      <h1 className="page-title">{tab === 'hitlist' ? 'The Hitlist' : tab === 'rob' ? 'Rob' : tab === 'bosses' ? 'Boss Fights' : tab === 'enemy' ? 'Public Enemy' : 'Fight'}</h1>
      <div className="subnav" style={{ marginBottom: 8 }}>
        <a className={tab !== 'hitlist' && tab !== 'rob' && tab !== 'bosses' && tab !== 'enemy' ? 'active' : ''} href="#/fight">Fight</a>
        <a className={tab === 'hitlist' ? 'active' : ''} href="#/fight/hitlist">Hitlist</a>
        <a className={tab === 'rob' ? 'active' : ''} href="#/fight/rob">Rob</a>
        <a className={tab === 'bosses' ? 'active' : ''} href="#/fight/bosses">Bosses{me.bossFight && <span className="badge">!</span>}</a>
        <a className={tab === 'enemy' ? 'active' : ''} href="#/fight/enemy">Public Enemy</a>
      </div>
      <div className="row" style={{ marginBottom: 8, gap: 16 }}>
        <span><StatIcon kind="attack" /> Attack strength: <b>{fmt(me.attackStrength)}</b></span>
        <span><StatIcon kind="defense" /> Defense strength: <b>{fmt(me.defenseStrength)}</b></span>
        <span className="grow" />
      </div>
      {tab === 'bosses' ? <BossesTab /> : tab === 'enemy' ? <PublicEnemyTab /> : me.level < r.protectedBelowLevel ? (
        <div className="flash bad"><div>You can start fighting, robbing and hunting bounties at level {r.protectedBelowLevel}. Do some jobs first.</div></div>
      ) : tab === 'hitlist' ? <Hitlist /> : tab === 'rob' ? <Rob /> : <FightList />}
    </div>
  );
}

/** "Eat a Pastrami Sandwich" button, shown on the Jobs page (it refills energy). */
export function SandwichButton() {
  const { me, act } = useGame();
  const ok = me.sandwichesLeftToday > 0 && me.energy < me.maxEnergy;
  return (
    <button className={`btn green small ${ok ? '' : 'disabled'}`}
      title={me.sandwichesLeftToday > 0 ? 'Refills all your energy. One a day.' : 'You already had one today. One a day.'}
      onClick={() => act('/api/items/use', { itemId: 'pastrami_sandwich' }, { success: () => ({ kind: 'good', text: 'You ate a Pastrami Sandwich. Your energy is full!' }) })}>
      Eat a Pastrami Sandwich ({me.sandwiches}){me.sandwichesLeftToday > 0 ? '' : ' · tomorrow'}
    </button>
  );
}

/** v0.8: wins in a row, with a pip for each win towards the next bonus. */
function StreakMeter() {
  const { me, content } = useGame();
  const every = content.rules.fightStreak.every;
  const n = me.fightStreak ?? 0;
  const into = n % every;
  return (
    <div className="streak-meter" title="Win fights you start in a row. Every 5th win in a row pays a bonus; 10 makes the news.">
      <span>Winning streak: <b className={n >= every ? 'gold' : ''}>{n}</b></span>
      <span className="pips">{Array.from({ length: every }, (_, i) => <i key={i} className={i < into || (n > 0 && into === 0) ? 'on' : ''} />)}</span>
      <span className="small dim">{n > 0 && into === 0 ? 'bonus paid! keep going' : `${every - into} more win${every - into === 1 ? '' : 's'} for a bonus`} · best {me.bestFightStreak ?? 0}</span>
    </div>
  );
}

function FightList() {
  const { me, content } = useGame();
  const attack = useAttack();
  const { data, reload } = useLoad(() => api.get('/api/fight/list'), []);
  const rivals = useLoad(() => api.get('/api/rivals'), [me.fightsWon, me.fightsLost]);
  const r = content.rules;
  const rivalIds = new Set((rivals.data?.rivals ?? []).map((x: any) => x.id));
  return (
    <>
      <StreakMeter />
      {rivals.data && rivals.data.rivals.length > 0 && (
        <Panel title="Your Rivals" right={<span className="small dim">beat them for +{r.rivalry.bonusPct}% cash</span>}>
          <div className="rival-cards">
            {rivals.data.rivals.map((x: any) => (
              <div key={x.id} className="rival-card">
                <Portrait seed={x.portrait} playerId={x.id} size={44} />
                <div className="grow">
                  <PlayerLink id={x.id} name={x.name} /> <span className="small dim">L{x.level}</span>
                  <div className="small">You <b className="good">{x.wins}</b> – <b className="bad">{x.losses}</b> them · {x.fightsThisWeek} fights this week</div>
                </div>
                <button className="btn small red" onClick={async () => { await attack(x.id, x.name, x.portrait); reload(); rivals.reload(); }}>Attack</button>
              </div>
            ))}
          </div>
        </Panel>
      )}
      <p className="muted">
        Your mafia of <b>{me.mafiaSize}</b> fights with you: each member carries your best weapon, armor and vehicle.
        Each fight costs {r.staminaPerFight} stamina. You need {r.minHealthToAttack} health to fight. Cash on hand can be stolen: <a onClick={() => go('bank')}>bank it</a>.{' '}
        <a className="small" onClick={reload}>Refresh list</a>
      </p>
      {me.robsLeftToday <= 0 && (
        <div className="flash bad"><div>You’ve already pulled your job for today. Lie low; you can rob again in about {Math.max(1, Math.ceil((me.nextRobAt - Date.now()) / 3_600_000))} hour(s).</div></div>
      )}
      {!data ? <Loading /> : data.targets.length === 0 ? (
        <div className="muted">Nobody around your level is healthy enough to fight right now. Check back soon.</div>
      ) : (
        <table className="list stack">
          <thead><tr><th></th><th>Opponent</th><th>Level</th><th>Mafia</th><th></th></tr></thead>
          <tbody>
            {data.targets.map((t: any) => (
              <tr key={t.id}>
                <td style={{ width: 40 }}><Portrait seed={t.portrait} playerId={t.id} size={32} /></td>
                <td>{rivalIds.has(t.id) && <span className="rival-tag">Rival</span>}<PlayerLink id={t.id} name={t.name} /><div className="small dim">{CLASS_INFO[t.charClass]?.name}</div></td>
                <td>{t.level}</td>
                <td>{t.mafia}</td>
                <td className="right"><button className="btn" onClick={async () => { await attack(t.id, t.name, t.portrait); reload(); }}>Attack</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Hitlist() {
  const { act, setFlash, me, content } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/hitlist'), [me?.fightsWon]);
  const hit = async (t: any) => {
    const r = await act('/api/hitlist/attack', { targetId: t.id });
    if (!r) return;
    floatAfter((r.bounty ?? 0) - (r.won ? 0 : r.cash ?? 0), 0);
    setFlash({
      kind: r.won ? 'good' : 'bad',
      title: r.bounty ? `You took out ${t.name} and collected the bounty!` : r.won ? `You hit ${t.name}!` : `${t.name} fought you off.`,
      text: (
        <>
          <FightClash key={Date.now()} mode="hit" left={{ seed: me.portrait, playerId: me.id, name: 'You' }} right={{ seed: t.portrait, playerId: t.id, name: t.name }} won={r.won} dealt={r.damageDealt ?? 0} taken={r.damageTaken ?? 0} />
          {r.bounty > 0 && <div className="money" style={{ fontSize: 14 }}>+{money(r.bounty)} bounty</div>}
          {r.won ? <>You dealt {r.damageDealt} damage. {t.name} has <b>{r.targetHealth}</b> health left.</> : <>You took {r.damageTaken} damage{r.cash ? ` and lost ${money(r.cash)}` : ''}.</>}
        </>
      ),
    });
    reload();
  };
  if (!data) return <Loading />;
  return (
    <div>
      <p className="muted">
        Players with a price on their head. Hitlist targets can be attacked even when wounded: whoever lands the hit that takes them to 0 health
        collects every bounty on them. Each attack costs 1 stamina. To put someone on the list, use the <b>Put a Hit Out</b> button on their profile
        (the agency charges a {content.rules.hitlistFeePct}% fee; unclaimed bounties are refunded after {content.rules.hitlistExpireDays} days).
      </p>
      {me.bountyOnMe > 0 && <div className="flash bad"><div>⚠ There’s a <b>{money(me.bountyOnMe)}</b> bounty on <b>you</b>. Keep your health up: anyone can hunt you until you’re taken out.</div></div>}
      {data.frenzy && <div className="flash good"><div><b>Bounty Frenzy!</b> Every bounty collected pays double, and placing a hit costs no fee.</div></div>}
      {data.targets.length > 0 && (
        <div className="wanted-row">
          {data.targets.slice(0, 3).map((t: any, i: number) => (
            <div key={t.id} className="wanted-poster" onClick={() => go(`profile/${t.id}`)}>
              <div className="wp-head">WANTED</div>
              <Portrait seed={t.portrait} playerId={t.id} size={64} />
              <div className="wp-name">{t.name}</div>
              <div className="wp-sub">{i === 0 ? 'Public enemy no. 1' : 'Dead or alive'}</div>
              <div className="wp-reward">{money(t.bounty)}</div>
            </div>
          ))}
        </div>
      )}
      {data.targets.length === 0 ? <div className="muted">Nobody is on the Hitlist right now.</div> : (
        <table className="list stack">
          <thead><tr><th></th><th>Target</th><th>Level</th><th>Health</th><th>Bounty</th><th></th></tr></thead>
          <tbody>
            {data.targets.map((t: any) => {
              const why = t.isMe ? 'That’s you!' : t.placedByMe ? 'Your own bounty' : t.sameMafia ? 'In your mafia' : t.health <= 0 ? 'Already taken out' : null;
              return (
                <tr key={t.id}>
                  <td style={{ width: 40 }}><Portrait seed={t.portrait} playerId={t.id} size={32} /></td>
                  <td><PlayerLink id={t.id} name={t.name} />{t.bounties > 1 && <div className="small dim">{t.bounties} bounties</div>}</td>
                  <td>{t.level}</td>
                  <td><div className="progress" style={{ width: 70 }}><div style={{ width: `${(t.health / t.maxHealth) * 100}%`, background: '#c22' }} /></div><span className="small">{t.health}/{t.maxHealth}</span></td>
                  <td className="money">{money(t.bounty)}</td>
                  <td className="right">{why ? <span className="small dim">{why}</span> : <button className="btn" onClick={() => hit(t)}>Attack</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {data.mine.length > 0 && (
        <Panel title="Your bounties">
          {data.mine.map((b: any) => <div key={b.id} className="feed-item"><span className="grow"><PlayerLink id={b.targetId} name={b.targetName} /></span><span className="money">{money(b.amount)}</span></div>)}
        </Panel>
      )}
      {data.allTimeWanted?.length > 0 && (
        <Panel title="Most Wanted of all time" right={<span className="small dim">total ever put on their head</span>}>
          {data.allTimeWanted.map((w: any, i: number) => (
            <div key={w.id} className="feed-item">
              <b className="gold" style={{ width: 18 }}>{i + 1}</b>
              <span className="grow"><PlayerLink id={w.id} name={w.name} /> <span className="small dim">L{w.level} · {w.count} contract{w.count === 1 ? '' : 's'}</span></span>
              <span className="money">{money(w.total)}</span>
            </div>
          ))}
        </Panel>
      )}
      {data.recent.length > 0 && (
        <Panel title="Recently collected">
          {data.recent.map((b: any, i: number) => (
            <div key={i} className="feed-item"><span className="grow"><PlayerLink id={b.hunterId} name={b.hunterName} /> took out <PlayerLink id={b.targetId} name={b.targetName} /></span><span className="money">{money(b.amount)}</span></div>
          ))}
        </Panel>
      )}
    </div>
  );
}

function Rob() {
  const { act, setFlash, content, me } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/rob/list'), []);
  const doRob = async (t: any) => {
    const r = await act('/api/rob', { targetId: t.id });
    if (!r) return;
    floatAfter(r.success ? r.cash : 0, r.success ? r.xp : 0, 900);
    setFlash({
      kind: r.success ? 'good' : 'bad',
      title: r.success ? `You robbed ${t.name}’s properties!` : `You got caught robbing ${t.name}.`,
      text: <><FightClash key={Date.now()} mode="rob" left={{ seed: me.portrait, playerId: me.id, name: 'You' }} right={{ seed: t.portrait, playerId: t.id, name: t.name }} won={r.success} dealt={0} taken={r.damageTaken ?? 0} />{r.success
        ? <>You got away with <span className="money">{money(r.cash)}</span> and <span className="xp">{r.xp} experience</span>.{r.levelUps.map((u: any) => <div key={u.level} className="gold">LEVEL UP! You are now level {u.level}.</div>)}</>
        : <>Their guards ran you off. You lost {r.damageTaken} health.</>}</>,
    });
    reload();
  };
  return (
    <div>
      <p className="muted">
        Sneak into someone’s properties and grab part of the income they haven’t collected yet. Costs {content.rules.robStaminaCost} stamina,
        and you can only pull off {content.rules.robsPerDay === 1 ? 'one robbery' : `${content.rules.robsPerDay} robberies`} a day, so pick your mark carefully.
        Your chance depends on your attack strength against their defense. Robbing earns a little XP and makes you a rival.
        Your own uncollected income can be robbed too, so <a onClick={() => go('territory')}>collect it often</a>.{' '}
        <a className="small" onClick={reload}>Refresh list</a>
      </p>
      {me.robsLeftToday <= 0 && (
        <div className="flash bad"><div>You’ve already pulled your job for today. Lie low; you can rob again in about {Math.max(1, Math.ceil((me.nextRobAt - Date.now()) / 3_600_000))} hour(s).</div></div>
      )}
      {!data ? <Loading /> : data.targets.length === 0 ? <div className="muted">No one near your level has properties worth robbing right now.</div> : (
        <table className="list stack">
          <thead><tr><th></th><th>Owner</th><th>Level</th><th>Properties</th><th>Loot</th><th></th></tr></thead>
          <tbody>
            {data.targets.map((t: any) => (
              <tr key={t.id}>
                <td style={{ width: 40 }}><Portrait seed={t.portrait} playerId={t.id} size={32} /></td>
                <td><PlayerLink id={t.id} name={t.name} /></td>
                <td>{t.level}</td>
                <td>{t.properties}</td>
                <td className={t.loot === 'big' ? 'money' : t.loot === 'medium' ? 'gold' : 'dim'}>{t.loot === 'big' ? '$$$ big' : t.loot === 'medium' ? '$$ medium' : '$ small'}</td>
                <td className="right"><button className={`btn ${me.stamina >= content.rules.robStaminaCost && me.robsLeftToday > 0 ? '' : 'disabled'}`} onClick={() => doRob(t)}>Rob</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
